import React, { useContext } from 'react';
import styled from 'styled-components';
import { SanityImage } from '@repo/ui-components/SanityImage';

import {
  SanityImageAsset,
  SanityImageCrop,
  SanityImageHotspot,
  SanityLink,
} from '@repo/models/gatsby-graphql-types';
import { LocaleContext } from '@repo/contexts/LocaleContext';
import { LocalizedLink } from '@repo/gatsby-localization-helpers';
import { LocaleCurrencyEnum } from '@repo/models/LocaleCurrencyEnum';
import getInternalLink from '@repo/utils/getInternalLink';
import breakpoints from '../../breakpoints';

const imageWidthCss = presentation => {
  switch (presentation) {
    case 'full-width':
    case 'content-width':
      return '100%';
    default:
      return 'auto';
  }
};

const StyledImage = styled(SanityImage)`
  display: block;
  margin: 0 auto;
  width: ${({ presentation }) => imageWidthCss(presentation)};
  max-width: 100%;
  margin-bottom: 1rem;
`;

type SanityLinkProps = SanityLink & {
  internalLink?: {
    slug?: {
      current?: string;
    };
    store?: {
      slug?: {
        current?: string;
      };
    };
  };
};

interface BlockContentImageProps {
  value: {
    asset?: SanityImageAsset;
    crop?: SanityImageCrop;
    hotspot?: SanityImageHotspot;
    alt?: string;
    presentation?: string;
    link?: SanityLinkProps;
  };
}

function BlockContentImage({ value }: Readonly<BlockContentImageProps>) {
  const localeContext = useContext(LocaleContext);
  const currency = localeContext?.currency ?? LocaleCurrencyEnum.USD;
  const { presentation, link } = value;

  let imageWidthPixels = presentation?.includes('full-width')
    ? 1920
    : parseInt(breakpoints.xl, 10);

  const originalWidth = value?.asset?.metadata?.dimensions?.width;
  if (imageWidthPixels > originalWidth) imageWidthPixels = originalWidth;

  if (value?.asset?._id && link?.linkType === 'external') {
    return (
      <a
        href={link.href}
        target={`${link.blank ? '_blank' : '_self'}`}
        rel="noopener noreferrer"
      >
        <StyledImage
          id={value.asset._id}
          alt={value.alt}
          loading="lazy"
          hotspot={value?.hotspot}
          crop={value?.crop}
          width={imageWidthPixels}
          presentation={presentation}
          sizes={`${imageWidthPixels}px`}
        />
      </a>
    );
  }

  if (value?.asset?._id && link?.linkType === 'internal') {
    return (
      <LocalizedLink
        to={getInternalLink({
          slug: `/${
            link?.internalLink?.slug?.current ??
            link?.internalLink?.store?.slug?.current ??
            link?.href
          }`,
          anchor: link?.anchor,
          currency,
        })}
        rel="noopener noreferrer"
      >
        <StyledImage
          id={value.asset._id}
          alt={value.alt}
          loading="lazy"
          hotspot={value?.hotspot}
          crop={value?.crop}
          width={imageWidthPixels}
          presentation={presentation}
          sizes={`${imageWidthPixels}px`}
        />
      </LocalizedLink>
    );
  }

  if (value?.asset?._id) {
    return (
      <StyledImage
        id={value.asset._id}
        alt={value.alt}
        loading="lazy"
        hotspot={value?.hotspot}
        crop={value?.crop}
        width={imageWidthPixels}
        presentation={presentation}
        sizes={`${imageWidthPixels}px`}
      />
    );
  }

  return null;
}

export default BlockContentImage;
